/* eslint-disable @nx/enforce-module-boundaries */
import { MailIcon } from '@mybridge/icons/MailIcon';
import { Avatar, Button, HStack, Link, Stack, Text, Box } from '@mybridge/ui';
import { forwardRef, useContext, useRef, useState } from 'react';
import { getUserFullName, getUserProfilePic } from 'v4/lib/commons';
import { MyProfileContext } from '../context';
import { useSelector } from 'react-redux';
import { MyNetworkContext } from 'v4/components/network/context';
import { useRouter } from 'next/router';
import { Divider, Flex, IconButton, Tooltip, useDisclosure, VStack } from '@chakra-ui/react';
import { ChatOutLined, HandShakeSimpleIcon, SimpleCloseIcon } from '@mybridge/icons';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useNumberFormatter } from '@mybridge/ui/custom-hooks/useNumberFormatter';
import { GlobalMatrixPopup } from 'v4/snippets/globalMatrixPopup';
import { getMatrixUserId, getRoomIdFromLS } from 'v4/utils/utils';
export const MyProfileViewsCount = forwardRef(({ ...props }, ref) => {
  const { myAnalytics, profileViewers } = useContext(MyProfileContext);
  const profileViewCount =
    myAnalytics?.find?.((ma) => ma?.title === 'Profile Views')?.total_count ??
    0;

  // const restCount = (profileViewers?.length ?? 0) - (profileViewCount ?? 0);
  const restCount = (profileViewers?.length ?? 0) - 3;

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [nullMatrixUser, setNullMatrixUser] = useState(null);
  const [confirmAction, setConfirmAction] = useState(() => () => {});
  const router = useRouter();
  const { t } = useTranslator();
  const [loadingButtonId, setLoadingButtonId] = useState(null);
  const { formatNumber } = useNumberFormatter();
  // Modify the handshake handler
  const handleHandshake = async (user) => {
    setLoadingButtonId(user.id);
    try {
      await handshakeUser?.(user);
    } finally {
      setLoadingButtonId(null);
    }
  };

  const { 
    isFriend,
    handshakeUser,
    followUser,
    isHandshakeRequested,
    isFollowed,
    handshakeLoading,
    handleHandshakeSendRevoke, 
    revokeHandshakeLoading,
    setSelectedUser,
  } = useContext(MyNetworkContext);

  const { userProfileInfo } = useSelector((state) => state?.userProfile);

  // Add a ref to track the trigger element
  const triggerRef = useRef(null);
  
  // Function to calculate position
  const getPopupPosition = () => {
    if (!triggerRef.current) return {};
    
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    
    // If more space below, show popup below
    if (spaceBelow >= 400 || spaceBelow > spaceAbove) {
      return {
        top: triggerRect.bottom + 5,
        left: triggerRect.left
      };
    }
    
    // Show popup above if more space there
    return {
      bottom: viewportHeight - triggerRect.top + 8,
      left: triggerRect.left
    };
  };

  const timerRef = useRef(null);

  const handleMouseEnter = () => {
    clearTimeout(timerRef.current);
    setIsPopupOpen(true);
  };

  const handleMouseLeave = () => {
    timerRef.current = setTimeout(() => {
      setIsPopupOpen(false);
    }, 1000);
  };

  // Handle handshake send/revoke
  const handleHandshakeSendRevokeAction = async (user) => {
    setLoadingButtonId(user.id);
    try {
      await handleHandshakeSendRevoke?.(user);
    } finally {
      setLoadingButtonId(null);
    }
  };

  return (
    <Stack spacing={4} {...props}>
      {profileViewers?.length && (
        <HStack alignItems="flex-start">
          <Text fontSize="14px" color="#5B5B5B">
            <Box as="span" fontSize="18px" verticalAlign="middle">
              {formatNumber(profileViewers?.length) || 0} {t('profile.profileViewers')}
            </Box>{' '}
            <Box>
              {t('profile.lookAtYouGo') || "Look at you go! People are noticing your profile."}
            </Box>
          </Text>
        </HStack>
      )}

      {profileViewers?.length && (
        <HStack 
          ref={triggerRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          justifyContent="flex-start" alignItems="center">
          {profileViewers?.slice?.(0, 3)?.map?.((pv, pvIndex) => (
            <Avatar
              border="3px solid white"
              name={getUserFullName(pv?.user)}
              ml={pvIndex ? -6 : 0}
              src={getUserProfilePic(pv?.user)}
              key={pv?.id}
            />
          ))}
          {/* {restCount > 0 ? ( */}
          <Text
            as={Link}
            href="/profile/network/viewers"
            color="#5B5B5B"
            fontSize="13px"
            fontWeight={400}
          >
            {restCount > 0 ? `+${formatNumber(restCount)}` : ``} {t('profile.viewingYourProfile') || "viewing your profile"}
          </Text>
          {/* ) : (
          ''
        )} */}
        </HStack>
      )}

      <HStack justifyContent="flex-start">
        <Button
          flex={1}
          as={Link}
          href={'/profile/network'}
          variant="secondary"
          // py={6}
          borderRadius={65}
          leftIcon={<MailIcon />}
          fontSize="14px"
          color="#3D5A80"
          alignItems="center"
        >
          {t('profile.manageInvitations') || "Manage Invitations"}
        </Button>
      </HStack>
      <HStack justifyContent="flex-start">
        <Link fontWeight={500} href="/profile/testimonials" color="#3D5A80" fontSize="14px">
          {t('profile.giveOrAskTestimonial') || "Give or Ask for a Testimonial"}
        </Link>
      </HStack>
      {isPopupOpen && (
          <Box
            position="fixed"
            // minH="400px"
            maxH="600px"
            w="400px"
            bg="white"
            boxShadow="lg"
            borderRadius="8px"
            box-shadow = "0px 0px 10px 0px #00000033"
            p={4}
            pt={0}
            zIndex={999}
            style={getPopupPosition()}
            onMouseEnter={() => clearTimeout(timerRef.current)}
            onMouseLeave={handleMouseLeave}
          >
            
            <Box position="relative" border="transparent">
            <Divider mb={4} border="transparent"/>
            { 
            <Box>
              <Flex justifyContent="space-between" alignItems="center" mb={4}>
                <Text fontWeight="400" fontSize="20px" color="#3D5A80" display="flex" justifyContent="center" width="100%">
                  {t('profile.profileViewers') || "Profile Viewers"}
                </Text>
                <IconButton
                  display={"flex"}
                  icon={<SimpleCloseIcon height="14px" width="14px" />}
                  aria-label={t('buttons.close') || "Close"}
                  variant={'unstyled'}
                  onClick={() => setIsPopupOpen(false)}
                />
              </Flex>
              <Box maxHeight="280px" overflowY="auto" p={2}>
              <VStack spacing={4} align="stretch">
                {profileViewers?.map((viewer, index) => (
                  <Flex key={index} alignItems="center" justifyContent="space-between">
                    <HStack spacing={3} cursor="pointer" onClick={() => userProfileInfo?.id === viewer?.user?.id ? router.push(`/profile/me`) : router.push(`/p/${viewer?.user?.slug}`)}>
                      <Avatar src={viewer?.user?.profile_pic} name={getUserFullName(viewer?.user)} />
                      <Text fontWeight="500" color="#5B5B5B" fontSize="15px">{getUserFullName(viewer?.user)}</Text>
                    </HStack>
                    <HStack spacing={2}>
                      {userProfileInfo?.id === viewer?.user?.id ? null :
                        !isFriend(viewer?.user) ? <HStack>
                          {isHandshakeRequested?.(viewer?.user) ? (
                            <Tooltip label={t('publicProfile.withdrawHandshake') || "Withdraw Handshake"}>
                              <IconButton
                                onClick={() => handleHandshakeSendRevokeAction(viewer?.user)}
                                _hover={{ bg: 'gray.100' }}
                                isLoading={loadingButtonId === viewer?.user?.id || revokeHandshakeLoading}
                                icon={<HandShakeSimpleIcon color="#F5AEAD" width="20" height="20" />}
                                w={'41px'}
                                height={'41px'}
                                border={'1px solid #00000033'}
                                cursor="pointer"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                borderRadius={'50%'}
                                variant="none"
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip zIndex="99999999" label={t('publicProfile.sendHandshakeRequest') || "Send Handshake Request"}>
                              <IconButton
                                w={'41px'}
                                _hover={{ bg: 'gray.100' }}
                                height={'41px'}
                                border={'1px solid #00000033'}
                                cursor="pointer"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                borderRadius={'50%'}
                                variant="none"
                                onClick={() => handleHandshake(viewer?.user)}
                                isLoading={loadingButtonId === viewer?.user?.id}
                                icon={<HandShakeSimpleIcon color="#94B096" width="20" height="20" />}
                              />
                            </Tooltip>
                          )}
                        </HStack> :
                        <Box
                          w={'41px'}
                          height={'41px'}
                          border={'1px solid #00000033'}
                          cursor="pointer"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          borderRadius={'50%'}
                          // onClick={(e) => {
                          //   router.push(`/profile/message?user=${viewer?.user?.matrix_user_id}`)
                          // }}
                          onClick={() => {
                            // const matrixUserId = getMatrixUserId(viewer?.user?.id);
                            
                            // if (!(viewer?.user?.connection?.chat_room_id || matrixUserId)) {
                            //   setConfirmAction(() => (newId) => {
                            //     router.push(`/profile/message?user=${newId}`);
                            //   });
                            //   onOpen();
                            // } else {
                              const roomId = getRoomIdFromLS(viewer?.user?.id);
                              router.push(`/profile/message?user=${roomId}`);
                            // }
                          }}
                          _hover={{ bg: 'gray.100' }}
                        >
                          <ChatOutLined/>
                        </Box>
                      }
                    </HStack>
                  </Flex>
                ))}
              </VStack>
              </Box>
            </Box>
            }
            </Box>
          </Box>
        )}
        <GlobalMatrixPopup
          isOpen={isOpen}
          onClose={onClose}
          onConfirm={confirmAction}
          selectedUserId={nullMatrixUser?.id}
        />
    </Stack>
  );
});
